/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import type { Theme } from '@coursera/cds-core';
import { Button, Dialog, InlineNotification, Link, Typography2 } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import CopyProfileLink from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/CopyProfileLink';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import { SLUG_MAX_COUNT } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/profileSlugUtils';
import { shouldEnableProfileSlugs } from 'bundles/employability-common/feature-flags/learnerProfileEpicFeatureFlags';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

const styles = {
  cont: (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(16)};
    align-items: flex-start;
  `,
  controls: (theme: Theme) => css`
    margin-bottom: ${theme.spacing(32)};
  `,
  message: (theme: Theme) => css`
    width: 100%;
    margin-bottom: ${theme.spacing(16)};
  `,
};

const E2E_SELECTORS = {
  GOT_IT: 'share-profile-got-it',
} as const;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onEditClick: () => void;
  currentSlug?: string;
  slugCount?: number;
  message?: string;
};

const PublicProfileShareLinkDialog: React.VFC<Props> = ({
  isOpen,
  onClose,
  onEditClick,
  currentSlug,
  slugCount = 0,
  message,
}) => {
  const { sharedEventingV3Data } = useContext(ProfileContext);

  const { HeadingGroup, Content, Actions } = Dialog;
  const slugsEnabled = shouldEnableProfileSlugs();
  const editAllowed = slugCount < SLUG_MAX_COUNT;

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_modal',
    {
      modal: {
        name: EVENTS.sharePublicProfileDialog,
      },
      ...sharedEventingV3Data,
    },
    /**
     * We need to add isOpen as a dependency because this modal component can render when the modal is closed.
     * When that happens, the ref will not mount. We need to rerun the visibility tracker when the modal opens
     * so the ref can be attached properly.
     */
    [isOpen]
  );

  return (
    <Dialog open={isOpen} onClose={onClose} width="large" variant="standard" aria-describedby="dialog-content">
      <HeadingGroup
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error: next-line
        supportText={
          <Typography2 component="p" variant="bodyPrimary">
            {_t(
              'Sharing your profile is a great way to stand out by showcasing your skills and accomplishments on Coursera.'
            )}
          </Typography2>
        }
      >
        {_t('Your profile link has been copied')}
      </HeadingGroup>
      <Content id="dialog-content" ref={visibilityTrackingRef}>
        <TrackedDiv
          trackClicks={false}
          trackingName={EVENTS.sharePublicProfileDialog}
          withVisibilityTracking
          requireFullyVisible={false}
          css={styles.cont}
        >
          {message && (
            <div css={styles.message}>
              <InlineNotification severity="success" aria-label={message}>
                {message}
              </InlineNotification>
            </div>
          )}

          <div css={[styles.cont, editAllowed && styles.controls]}>
            <CopyProfileLink currentSlug={currentSlug} />
            {slugsEnabled && editAllowed && (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <Link component="button" style={{ display: 'block' }} onClick={() => onEditClick()}>
                Customize your link
              </Link>
            )}
          </div>

          {slugsEnabled && !editAllowed && (
            <InlineNotification severity="warning">
              <div>
                {_t(
                  'You’ve reached the maximum number of times that you can customize your link. You can’t make any more changes.'
                )}
              </div>
            </InlineNotification>
          )}

          <InlineNotification severity="information" aria-label={_t('Sharing of personal information')}>
            {_t(
              'Please note that by sharing your profile link, you consent to sharing your profile information with anyone who has access to your link. You can change your visibility settings anytime.'
            )}
          </InlineNotification>
        </TrackedDiv>
      </Content>
      <Actions>
        <Button
          variant="primary"
          onClick={onClose}
          component={TrackedButton}
          // not tracked in eventing v3
          trackingName="profile_close_public_share_profile"
          requireFullyVisible={false}
          withVisibilityTracking={true}
          data-e2e={E2E_SELECTORS.GOT_IT}
        >
          {_t('Got it')}
        </Button>
      </Actions>
    </Dialog>
  );
};

export default PublicProfileShareLinkDialog;
